import { useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import type { LayoutPayload } from "@/types/layout";
import type { AccountTab } from "@/utils/crossAppMenu";
import { B2B_USER_TYPE } from "@/types/b2bAccount";
import { buildLoginUrl } from "@/utils/auth";
import { getLocale } from "@/utils/locale-store";
import { localizeUrl } from "@/utils/url";
import { goAccountPath, toAccountTab } from "@/utils/crossAppMenu";

/**
 * The tab this route resolves to always wins over one the caller's link
 * carried, so a stale bookmark to a since-renamed tab still lands somewhere
 * valid; any other query params (a promo code, an analytics tag) pass through
 * untouched.
 */
function accountQuery(search: string, tab: AccountTab): string {
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  return `?${params.toString()}`;
}

/**
 * Stable entry point for the storefront from another app. Public: browsing
 * never needs a member, and the caller cannot know the visitor's locale, so
 * the route adds it. The incoming query string (e.g. a campaign tag) rides
 * along unchanged.
 */
export function GoShopRoute() {
  const { search } = useLocation();
  return (
    <Navigate
      to={`${localizeUrl("/", getLocale().language)}${search}`}
      replace
    />
  );
}

interface GoCartRouteProps {
  /** Localized path of the cart page (from the CMS). */
  cartPath: string;
}

/**
 * Stable entry point for the cart from another app; WeHunt Web's top bar
 * links here. The cart page's slug is editor-controlled and localized, so
 * only this app can resolve it. Public like the storefront: a guest has a
 * cart too. The incoming query string rides along unchanged.
 */
export function GoCartRoute({ cartPath }: GoCartRouteProps) {
  const { search } = useLocation();
  return <Navigate to={`${cartPath}${search}`} replace />;
}

interface GoAccountRouteProps {
  layoutData: LayoutPayload;
  /** Localized path of the B2C my-account page (from the CMS). */
  myAccountPath: string;
  /** Localized path of the B2B account page (from the CMS). */
  b2bMyAccountPath: string;
}

/**
 * Stable entry point for a member's account from another app; WeHunt Web's
 * menu links here. The CMS decides the my-account page's path per locale, so
 * nothing outside this app can know it, and only this app can see whether the
 * visitor already holds a member cookie. A member goes straight to the tab. A
 * guest goes through login with this very route as the return path, so the
 * round-trip lands here again and takes the member branch — the sso round-trip
 * therefore happens only when it is actually needed.
 */
export function GoAccountRoute({
  layoutData,
  myAccountPath,
  b2bMyAccountPath,
}: GoAccountRouteProps) {
  const { tab: rawTab } = useParams();
  const { search } = useLocation();
  const isB2b = layoutData.userType === B2B_USER_TYPE;
  const tab = toAccountTab(rawTab, isB2b);
  // The tab already lives in the path segment here, so the return path just
  // carries whatever else the caller's link had (a promo code, an analytics
  // tag) through the login round-trip untouched.
  const loginUrl = layoutData.isUserAuthenticated
    ? null
    : buildLoginUrl({ returnPath: `${goAccountPath(tab)}${search}` });

  useEffect(() => {
    // The login URL is on another origin (the API or the identity server), so
    // the router cannot take us there; the browser has to.
    if (loginUrl) window.location.assign(loginUrl);
  }, [loginUrl]);

  if (loginUrl) return null;

  const accountPath = isB2b ? b2bMyAccountPath : myAccountPath;
  return <Navigate to={`${accountPath}${accountQuery(search, tab)}`} replace />;
}
